"use client";

import { useRouter } from "next/navigation";
import { ThemeProvider } from "next-themes";
import { RouterProvider } from "@heroui/react";
import { Toast } from "@heroui/react";
import type { ReactNode } from "react";

declare module "react-aria-components" {
  interface RouterConfig {
    routerOptions: NonNullable<
      Parameters<ReturnType<typeof useRouter>["push"]>[1]
    >;
  }
}

/**
 * HeroUI v3 removed `HeroUIProvider`. Two things still need wiring at the root:
 *
 *  - `RouterProvider`, so HeroUI/React Aria components that navigate (Link,
 *    menu items, breadcrumbs) go through the Next.js client router instead of
 *    triggering a full page load
 *  - the toast region, which is a single mount point the `toast()` queue
 *    renders into
 */
export function Providers({
  children,
  nonce,
}: {
  children: ReactNode;
  /**
   * Passed down from the root layout so next-themes can stamp it on the
   * inline anti-flash script it injects. Without it that script is the one
   * thing our CSP blocks, and the page paints in the wrong theme before
   * correcting itself.
   */
  nonce?: string;
}) {
  const router = useRouter();

  return (
    <ThemeProvider
      nonce={nonce}
      attribute="class"
      // First visit follows the OS. This was pinned to light on the argument
      // that the product is a light interface first and a dark-mode laptop
      // landed people in an untuned theme; the dark tokens have since been
      // filled in across `globals.css` and the components, so respecting the
      // system setting no longer means shipping someone a half-finished view.
      //
      // `enableSystem` is what makes "system" a storable value rather than
      // just a default: until the toggle is touched the stored preference
      // stays "system" and tracks the OS live, including a change made while
      // the tab is open. Pressing the toggle writes an explicit light or dark
      // and that wins from then on.
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <RouterProvider navigate={router.push}>
        {children}
        <Toast.Provider placement="bottom end" />
      </RouterProvider>
    </ThemeProvider>
  );
}
